// =====================================================================================
// INVRT Radar — web console — consent banner designer — the form
//
// A controlled form over BannerFormState. It renders fields and errors; it owns no state,
// no validation and no network — BannerStudio does those, so the same fields serve the
// read-only view a non-administrator gets (every control disabled) without a second
// component.
// =====================================================================================

import type { ReactNode } from "react";
import { Input, Select, Textarea } from "@/components/ui/input";
import type { BannerLanguage, BannerPosition } from "@/lib/types.c1";
import {
  BANNER_LANGUAGES,
  BANNER_LIMITS,
  BANNER_POSITIONS,
  type BannerFormErrors,
  type BannerFormState,
  isHexColour,
  LANGUAGE_LABELS,
  POSITION_LABELS,
} from "./bannerForm";

export interface BannerDesignerProps {
  value: BannerFormState;
  errors: BannerFormErrors;
  disabled: boolean;
  onChange: (next: BannerFormState) => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3 rounded-card border border-line p-4">
      <legend className="px-1 text-sm font-semibold text-ink">{title}</legend>
      {children}
    </fieldset>
  );
}

function ColourField({
  label,
  value,
  error,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  error?: string | undefined;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const swatch = isHexColour(value.trim()) ? value.trim().toLowerCase() : "#000000";
  return (
    <div className="flex items-end gap-2">
      <div className="min-w-0 flex-1">
        <Input
          label={label}
          value={value}
          disabled={disabled}
          spellCheck={false}
          maxLength={7}
          placeholder="#405bd8"
          onChange={(event) => onChange(event.target.value)}
          {...(error ? { error } : {})}
        />
      </div>
      <input
        type="color"
        aria-label={`${label} picker`}
        value={swatch}
        disabled={disabled}
        className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-line bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-60"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function counter(text: string, limit: number): string {
  return `${text.length} / ${limit}`;
}

export function BannerDesigner({ value, errors, disabled, onChange }: BannerDesignerProps) {
  const setTheme = (key: keyof BannerFormState["theme"], next: string) =>
    onChange({ ...value, theme: { ...value.theme, [key]: next } });
  const setCopy = (key: keyof BannerFormState["copy"], next: string) =>
    onChange({ ...value, copy: { ...value.copy, [key]: next } });
  const setLinks = (key: keyof BannerFormState["links"], next: string) =>
    onChange({ ...value, links: { ...value.links, [key]: next } });
  const setBehaviour = <K extends keyof BannerFormState["behaviour"]>(
    key: K,
    next: BannerFormState["behaviour"][K],
  ) => onChange({ ...value, behaviour: { ...value.behaviour, [key]: next } });

  return (
    <div className="flex flex-col gap-4">
      <Section title="Layout">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Position"
            value={value.position}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, position: event.target.value as BannerPosition })}
            {...(errors.position ? { error: errors.position } : {})}
          >
            {BANNER_POSITIONS.map((position) => (
              <option key={position} value={position}>
                {POSITION_LABELS[position]}
              </option>
            ))}
          </Select>
          <Select
            label="Language"
            value={value.language}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, language: event.target.value as BannerLanguage })}
            {...(errors.language ? { error: errors.language } : {})}
          >
            {BANNER_LANGUAGES.map((language) => (
              <option key={language} value={language}>
                {LANGUAGE_LABELS[language]}
              </option>
            ))}
          </Select>
          <Input
            label="Corner radius (px)"
            type="number"
            inputMode="numeric"
            min={BANNER_LIMITS.radius.min}
            max={BANNER_LIMITS.radius.max}
            step={1}
            value={value.theme.border_radius_px}
            disabled={disabled}
            onChange={(event) => setTheme("border_radius_px", event.target.value)}
            hint="0 to 24. Bars ignore it; cards and the centre box use it."
            {...(errors["theme.border_radius_px"] ? { error: errors["theme.border_radius_px"] } : {})}
          />
          <Input
            label="Logo URL"
            type="url"
            value={value.logo_url}
            disabled={disabled}
            placeholder="https://example.com/logo.svg"
            onChange={(event) => onChange({ ...value, logo_url: event.target.value })}
            hint="Optional. An https:// image, shown beside the title."
            {...(errors.logo_url ? { error: errors.logo_url } : {})}
          />
        </div>
      </Section>

      <Section title="Colours">
        <div className="grid gap-3 sm:grid-cols-2">
          <ColourField
            label="Background colour"
            value={value.theme.background}
            error={errors["theme.background"]}
            disabled={disabled}
            onChange={(next) => setTheme("background", next)}
          />
          <ColourField
            label="Text colour"
            value={value.theme.text}
            error={errors["theme.text"]}
            disabled={disabled}
            onChange={(next) => setTheme("text", next)}
          />
          <ColourField
            label="Accent colour"
            value={value.theme.accent}
            error={errors["theme.accent"]}
            disabled={disabled}
            onChange={(next) => setTheme("accent", next)}
          />
          <ColourField
            label="Text on accent"
            value={value.theme.accent_text}
            error={errors["theme.accent_text"]}
            disabled={disabled}
            onChange={(next) => setTheme("accent_text", next)}
          />
        </div>
      </Section>

      <Section title="Copy">
        <Input
          label="Title"
          value={value.copy.title}
          disabled={disabled}
          maxLength={BANNER_LIMITS.title}
          onChange={(event) => setCopy("title", event.target.value)}
          hint={counter(value.copy.title, BANNER_LIMITS.title)}
          {...(errors["copy.title"] ? { error: errors["copy.title"] } : {})}
        />
        <Textarea
          label="Body"
          value={value.copy.body}
          disabled={disabled}
          maxLength={BANNER_LIMITS.body}
          rows={4}
          onChange={(event) => setCopy("body", event.target.value)}
          hint={counter(value.copy.body, BANNER_LIMITS.body)}
          {...(errors["copy.body"] ? { error: errors["copy.body"] } : {})}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Accept button"
            value={value.copy.accept}
            disabled={disabled}
            maxLength={BANNER_LIMITS.accept}
            onChange={(event) => setCopy("accept", event.target.value)}
            hint={counter(value.copy.accept, BANNER_LIMITS.accept)}
            {...(errors["copy.accept"] ? { error: errors["copy.accept"] } : {})}
          />
          <Input
            label="Reject button"
            value={value.copy.reject}
            disabled={disabled}
            maxLength={BANNER_LIMITS.reject}
            onChange={(event) => setCopy("reject", event.target.value)}
            hint={counter(value.copy.reject, BANNER_LIMITS.reject)}
            {...(errors["copy.reject"] ? { error: errors["copy.reject"] } : {})}
          />
          <Input
            label="Privacy policy link label"
            value={value.copy.manage}
            disabled={disabled}
            maxLength={BANNER_LIMITS.manage}
            placeholder="Privacy policy"
            onChange={(event) => setCopy("manage", event.target.value)}
            hint={counter(value.copy.manage, BANNER_LIMITS.manage)}
            {...(errors["copy.manage"] ? { error: errors["copy.manage"] } : {})}
          />
        </div>
      </Section>

      <Section title="Links">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Privacy policy URL"
            type="url"
            value={value.links.privacy_policy_url}
            disabled={disabled}
            placeholder="https://example.com/privacy"
            onChange={(event) => setLinks("privacy_policy_url", event.target.value)}
            hint="Required. Must start with https://."
            {...(errors["links.privacy_policy_url"]
              ? { error: errors["links.privacy_policy_url"] }
              : {})}
          />
          <Input
            label="Cookie policy URL"
            type="url"
            value={value.links.cookie_policy_url}
            disabled={disabled}
            placeholder="https://example.com/cookies"
            onChange={(event) => setLinks("cookie_policy_url", event.target.value)}
            hint="Optional."
            {...(errors["links.cookie_policy_url"]
              ? { error: errors["links.cookie_policy_url"] }
              : {})}
          />
        </div>
      </Section>

      <Section title="Behaviour">
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
            checked={value.behaviour.reject_button}
            disabled={disabled}
            onChange={(event) => setBehaviour("reject_button", event.target.checked)}
          />
          Show a reject button
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
            checked={value.behaviour.block_until_choice}
            disabled={disabled}
            onChange={(event) => setBehaviour("block_until_choice", event.target.checked)}
          />
          Require a choice before the banner closes
        </label>
        <div className="max-w-xs">
          <Input
            label="Ask again after (days)"
            type="number"
            inputMode="numeric"
            min={BANNER_LIMITS.reconsent_days.min}
            max={BANNER_LIMITS.reconsent_days.max}
            step={1}
            value={value.behaviour.reconsent_days}
            disabled={disabled}
            onChange={(event) => setBehaviour("reconsent_days", event.target.value)}
            hint="30 to 730. A stored choice expires after this many days."
            {...(errors["behaviour.reconsent_days"]
              ? { error: errors["behaviour.reconsent_days"] }
              : {})}
          />
        </div>
      </Section>
    </div>
  );
}
